<script setup lang="ts">
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { computed, ref, watch } from 'vue';
import { getReviewStatistics, type ReviewStatistics } from '@/api';
import StatisticsKpiGrid, {
  type StatisticsKpiTile,
} from '@/components/admin/StatisticsKpiGrid.vue';
import {
  buildStatisticsDateQuery,
  formatStatisticsNumber,
  type StatisticsFiltersProps,
} from '@/utils/adminStatistics';

const props = defineProps<StatisticsFiltersProps>();
const message = useGaiaToast();

const data = ref<ReviewStatistics | undefined>(undefined);
const loading = ref(false);

const kpiTiles = computed<StatisticsKpiTile[]>(() => {
  if (data.value === undefined) {
    return [];
  }

  return [
    { label: 'Total reviews', value: formatStatisticsNumber(data.value.total_reviews) },
    {
      label: 'Avg reviews / org',
      value: formatStatisticsNumber(data.value.average_reviews_per_organisation),
    },
    {
      label: 'Avg prompts / review',
      value: formatStatisticsNumber(data.value.average_prompts_per_review),
    },
    {
      label: 'Avg files / review',
      value: formatStatisticsNumber(data.value.average_files_per_review),
    },
  ];
});

async function fetchStatistics(): Promise<void> {
  loading.value = true;
  try {
    const response = await getReviewStatistics({
      query: {
        included_org_uids: props.includedOrgUids,
        excluded_org_uids: props.excludedOrgUids,
        ...buildStatisticsDateQuery(props.dateRange),
      },
    });
    if (response.response?.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    message.error('Failed to load review statistics');
    logger.error('Failed to load review statistics:', error);
  } finally {
    loading.value = false;
  }
}

watch(props, fetchStatistics, { deep: true, immediate: true });
</script>

<template>
  <n-spin :show="loading">
    <statistics-kpi-grid v-if="data !== undefined" :tiles="kpiTiles" />
    <div v-else style="min-height: 200px" />
  </n-spin>
</template>

<style scoped></style>
